/* eslint-disable react/jsx-fragments */
import React from "react";
import PropTypes from "prop-types";
import Modal from "reactstrap/lib/Modal";
import ModalHeader from "reactstrap/lib/ModalHeader";
import ModalBody from "reactstrap/lib/ModalBody";
import ModalFooter from "reactstrap/lib/ModalFooter";

import {
  gatherClasses,
  gatherConditionalProps,
  isFunction
} from "../../lib/utils";
import Button from "../button/button";
import Input from "../input/input";
import Label from "../label/label";
import InputFeedback from "../inputFeedback/inputFeedback";
import { handleIERenderingIssues } from "../../lib/modalUtils";

class ConfirmationModal extends React.Component {
  constructor(props) {
    super(props);
    const { emptyReasonError, confirmReasonRequired } = props;
    this.ref = React.createRef();
    this.state = {
      reasonInputValue: "",
      isReasonInputTouched: false,
      isReasonInputInvalid: !!confirmReasonRequired,
      invalidReasonErrorMessage: emptyReasonError
    };
  }

  onModalOpen = () => {
    const { onOpened } = this.props;

    handleIERenderingIssues(this.ref.current);

    if (isFunction(onOpened)) {
      onOpened();
    }
  };

  onModalClose = () => {
    const { confirmReasonRequired, emptyReasonError, onClosed } = this.props;
    if (confirmReasonRequired) {
      this.setState({
        reasonInputValue: "",
        isReasonInputTouched: false,
        isReasonInputInvalid: true,
        invalidReasonErrorMessage: emptyReasonError
      });
    }

    if (isFunction(onClosed)) {
      onClosed();
    }
  };

  handleReasonChange = e => {
    const inputValue = e.target.value;
    const invalidReasonErrorMessage =
      this.getInvalidReasonErrorMessage(inputValue);

    this.setState({
      reasonInputValue: e.target.value,
      isReasonInputInvalid: invalidReasonErrorMessage !== null,
      invalidReasonErrorMessage
    });
  };

  handleReasonBlur = () => {
    const { confirmReasonRequired } = this.props;

    if (confirmReasonRequired) {
      this.setState({ isReasonInputTouched: true });
    }
  };

  getInvalidReasonErrorMessage = value => {
    const {
      reasonMinLength,
      reasonMaxLength,
      emptyReasonError,
      invalidReasonLengthError,
      reasonRegex,
      reasonRegexError
    } = this.props;
    const trimValue = value.trim();

    if (trimValue === "") {
      return emptyReasonError;
    }

    if (reasonRegex && reasonRegexError && !reasonRegex.test(trimValue)) {
      return reasonRegexError;
    }

    if (
      trimValue.length < reasonMinLength ||
      trimValue.length > reasonMaxLength
    ) {
      return invalidReasonLengthError;
    }

    return null;
  };

  handleContinue = e => {
    const { onContinue, confirmReasonRequired } = this.props;
    let reasonText = null;

    if (confirmReasonRequired) {
      const { reasonInputValue } = this.state;
      const invalidReasonErrorMessage =
        this.getInvalidReasonErrorMessage(reasonInputValue);

      if (invalidReasonErrorMessage !== null) {
        this.setState({
          invalidReasonErrorMessage,
          isReasonInputTouched: true,
          isReasonInputInvalid: true
        });
        return;
      }

      reasonText = reasonInputValue;
    }

    if (isFunction(onContinue)) {
      onContinue(e, reasonText);
    }
  };

  render() {
    const {
      header,
      toggle,
      message,
      secondaryMessage,
      cancelButtonText,
      continueButtonText,
      continueButtonType,
      dataTestId,
      closeOnEscape,
      bodyClassName,
      body,
      confirmReasonRequired,
      reasonLabel,
      reasonPlaceholder
    } = this.props;
    const {
      reasonInputValue,
      invalidReasonErrorMessage,
      isReasonInputTouched,
      isReasonInputInvalid
    } = this.state;

    return (
      <Modal
        data-testid={dataTestId}
        innerRef={this.ref}
        centered
        className={gatherClasses(this.props, "confirmation-modal")}
        keyboard={closeOnEscape}
        {...gatherConditionalProps(
          this.props,
          "isOpen",
          "size",
          "autoFocus",
          "backdrop",
          "toggle",
          "onClosed"
        )}
        onOpened={this.onModalOpen}
        onClosed={this.onModalClose}
      >
        {header && <ModalHeader toggle={toggle}>{header}</ModalHeader>}

        <ModalBody className={bodyClassName}>
          {isFunction(body) ? (
            body()
          ) : (
            <React.Fragment>
              <div aria-label={message}>{message}</div>
              {secondaryMessage && (
                <div>
                  <br />
                  <div>{secondaryMessage}</div>
                </div>
              )}
            </React.Fragment>
          )}
          {confirmReasonRequired && (
            <React.Fragment>
              <Label text={reasonLabel} className="oui-mt-3" muted />
              <Input
                type="textarea"
                value={reasonInputValue}
                placeholder={reasonPlaceholder}
                onChange={this.handleReasonChange}
                onBlur={this.handleReasonBlur}
                isInvalid={isReasonInputInvalid && isReasonInputTouched}
              />
              {isReasonInputInvalid && isReasonInputTouched && (
                <InputFeedback show message={invalidReasonErrorMessage} />
              )}
            </React.Fragment>
          )}
        </ModalBody>

        <ModalFooter>
          <Button label={cancelButtonText} onClick={toggle} />
          <Button
            label={continueButtonText}
            onClick={this.handleContinue}
            type={continueButtonType}
            disabled={
              confirmReasonRequired &&
              isReasonInputInvalid &&
              isReasonInputTouched
            }
          />
        </ModalFooter>
      </Modal>
    );
  }
}

ConfirmationModal.propTypes = {
  isOpen: PropTypes.bool,
  header: PropTypes.string,
  closeOnEscape: PropTypes.bool,
  body: PropTypes.func,
  message: PropTypes.string,
  secondaryMessage: PropTypes.string,
  cancelButtonText: PropTypes.string,
  continueButtonText: PropTypes.string,
  className: PropTypes.string,
  bodyClassName: PropTypes.string,
  size: PropTypes.oneOf(["lg", "md", "sm"]),
  onContinue: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired,
  onOpened: PropTypes.func,
  onClosed: PropTypes.func,
  continueButtonType: PropTypes.oneOf(["primary", "danger"]),
  dataTestId: PropTypes.string,
  backdrop: PropTypes.string,
  confirmReasonRequired: PropTypes.bool,
  reasonLabel: PropTypes.string,
  reasonPlaceholder: PropTypes.string,
  reasonMinLength: PropTypes.number,
  reasonMaxLength: PropTypes.number,
  emptyReasonError: PropTypes.string,
  invalidReasonLengthError: PropTypes.string,
  reasonRegex: PropTypes.instanceOf(RegExp),
  reasonRegexError: PropTypes.string
};

ConfirmationModal.defaultProps = {
  isOpen: false,
  header: "Confirmation",
  secondaryMessage: "",
  cancelButtonText: "Cancel",
  continueButtonText: "Continue",
  className: "",
  size: "sm",
  // eslint-disable-next-line react/default-props-match-prop-types
  onContinue: () => {},
  // eslint-disable-next-line react/default-props-match-prop-types
  toggle: () => {},
  bodyClassName: null,
  closeOnEscape: false,
  backdrop: "static",
  continueButtonType: "primary",
  dataTestId: "orion-confirmation-modal",
  confirmReasonRequired: false,
  reasonLabel: "Reason for deletion",
  reasonPlaceholder: "Please enter your comments here.",
  reasonMinLength: 10,
  reasonMaxLength: 150,
  emptyReasonError: "Please enter a reason.",
  invalidReasonLengthError: "Reason must be 10 to 150 characters long."
};

ConfirmationModal.displayName = "OrionConfirmationModal";

export default ConfirmationModal;
